import React, { useEffect, useRef } from "react";
import styles from "./styles.module.scss";
import LazyLoader from "@/components/hoc/LazyLoader";
import Head from "next/head";
import SchemaLoader from "@/components/hoc/SchemaLoader";
import { WebsiteSchema } from "@/constants/schema";
import Config from "../../../config";
import { getStaticPath } from "../../../utils";
import { useRouter } from "next/router";
import { cleverTapTrigger } from "../../../tracking/segment";
import page_data from "../../../data/page_data";
import FeatureImageBanner from "../../../components/common/FeatureImageBanner";
import FeatureTwoRowComponent from "../../../components/common/FeatureTwoRowComponent";
import featureStyles from "../../../styles/modules/common/features.module.scss";
import BannerWithGraphic from "../../../components/common/BannerWithGraphic";
import Section from "../../../components/common/Section";
import Form from "@/components/enterprise-landingpage/Form";
import Link from "next/link";
import {
  EnterpriseBreadCrumbSchema,
  EnterpriseOrganizationSchema,
} from "@/constants/enterprise";

const Fetcher = async () => {
  const data = {
    meta_tag_data: page_data["landingpage/enterprise"]?.meta_tag || [],
    page_meta: page_data["landingpage/enterprise"]?.page_meta || {},
  };

  return data;
};

export async function getServerSideProps() {
  const data = await Fetcher();
  return {
    props: {
      ...data,
      pageName: "enterprise-landingpage",
    },
  };
}

const EnterpriseLandingPage = (props: any) => {
  const router = useRouter();
  const BASE_URL = Config.BASE_URL;
  const OG_IMAGE = getStaticPath("/static/images/why-onsitego.svg");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const cached_props: any = props || {};
  const features: any = cached_props?.page_meta?.features || {};

  return (
    <>
      <Head>
        <title>{cached_props?.meta_tag_data?.tag_title}</title>
        <link rel="icon" href="/favicon.ico" />
        <meta
          name="description"
          content={cached_props?.meta_tag_data?.tag_description}
        ></meta>
        <meta
          property="og:title"
          content={cached_props?.meta_tag_data?.tag_title}
        />
        <meta
          property="og:description"
          content={cached_props?.meta_tag_data?.tag_description}
        />
        <meta property="og:url" content={`${BASE_URL}${router.asPath}`} />
        <meta property="og:image" content={OG_IMAGE} />
        <link rel="canonical" href={`${BASE_URL}${router.asPath}`}></link>
        <SchemaLoader schema={WebsiteSchema} />
        <SchemaLoader schema={EnterpriseBreadCrumbSchema} />
        <SchemaLoader schema={EnterpriseOrganizationSchema} />
      </Head>
      <p className={`${styles.breadcrumb} container`}>
        <Link href="/">Home</Link> / Enterprise
      </p>

      {/* Banner Section */}
      <BannerWithGraphic
        title={cached_props.page_meta.bannerImageData.heading}
        subtitle={cached_props.page_meta.bannerImageData.subheading}
        cta_button_title={cached_props.page_meta.bannerImageData?.cta}
        banner_img={cached_props.page_meta.bannerImageData.banner_image}
        categories={""}
      />
      <div className="odd-even-start-with-gray">
        {/* stats section */}
        <Section
          title={cached_props?.page_meta?.stats?.title}
          className={styles.statsSection}
        >
          <div className={`${styles.stats} container`}>
            {cached_props?.page_meta?.stats?.items.map((item, index) => (
              <div key={index} className={styles.statItem}>
                <div className={styles.statNumber}>{item.number}</div>
                <div className={styles.statLabel}>{item.label}</div>
              </div>
            ))}
          </div>
        </Section>

        {/* Features Section */}
        <FeatureTwoRowComponent
          withSubtitle={false}
          features={features.twoRowFeatures.rowFeatures || []}
          title={features.twoRowFeatures.title || ""}
        />

        {/* Plans Section */}
        <Section
          title={features.plans.title}
          tagline={features.plans.description}
          className={styles.PlanSection}
        >
          <div className={`${styles.plans} container`}>
            {features.plans.features.map((plan, index) => (
              <div key={index} className={styles.planItem}>
                <h4 className={styles.planTitle}>{plan.title}</h4>
                {plan.items.map((item, fIndex) => (
                  <div className={styles.planFeatures} key={fIndex}>
                    <div className={styles.label}>{item.label}</div>
                    <div
                      className={styles.display}
                      value-type={item.value_type}
                      is-valued={item.value ? "true" : "false"}
                    >
                      {item.display}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </Section>

        {/* Why Choose Us Section */}
        <FeatureImageBanner
          title={features.why_choose_us?.title || ""}
          withSubtitle={false}
          features={features?.why_choose_us?.features || ""}
          className={`${styles.why_choose_us}`}
          imageSizeClass={featureStyles.amc__circle_image}
          isRoundShape
        />

        {/* Portal Section */}
        <Section
          title={features.portal.title}
          tagline={features.portal.description}
          className={styles.PortalSection}
        >
          <div className={`${styles.portal} container`}>
            {features.portal.features.map((feature, index) => (
              <div key={index} className={styles.portalItem}>
                <h4 className={styles.portalTitle}>{feature.title}</h4>
                <ul>
                  {feature.points.map((point, fIndex) => (
                    <li>{point}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Section>

        {/* Contact Us Section */}
        <section className={styles.ContactUs} id="plan-selection">
          <div className={styles.contactUsForm}>
            <Form />
          </div>
          <div className={styles.contactUsImg}></div>
        </section>
      </div>
    </>
  );
};

export default EnterpriseLandingPage;
